/**
 * Google Analytics (Theme settings → Analytics), skipped for our own visits
 * (see below). Loads gtag after the page
 * has finished, so it never competes with the pictures, and counts:
 * - view_artwork / view_installation: each picture shown in the lightbox
 *   (title, artist, show)
 * - artwork_time: seconds spent on that picture, sent on moving on or closing
 * - zoom_artwork: zooming into a picture's detail
 * - inquire_click: arriving at the Inquire form from an artwork's link
 * - generate_lead / sign_up: an inquiry or newsletter form sent
 *   (the success message carries data-analytics-event)
 * Page views and outbound links (RSVP, booking) are GA's own measurement.
 */
(() => {
  const id = document.currentScript?.dataset.measurementId;
  if (!id) return;

  // Our own visits aren't counted: ?internal=1 marks this browser as ours
  // (?internal=0 clears it), and preview themes and automated browsers
  // (screenshots, site checks) never load GA.
  const internalKey = 'luckygirl-internal';
  const internalParam = new URLSearchParams(window.location.search).get('internal');
  let internal = false;
  try {
    if (internalParam === '1') localStorage.setItem(internalKey, '1');
    if (internalParam === '0') localStorage.removeItem(internalKey);
    internal = localStorage.getItem(internalKey) === '1';
  } catch {
    internal = internalParam === '1';
  }
  const preview = window.Shopify?.theme?.role && window.Shopify.theme.role !== 'main';
  if (internal || preview || navigator.webdriver) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  gtag('js', new Date());
  gtag('config', id);

  const loadTag = () => {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.append(script);
  };
  if (document.readyState === 'complete') loadTag();
  else window.addEventListener('load', loadTag, { once: true });

  const artworkParams = (item) => ({
    artwork: item.dataset.alt || '',
    artist: item.dataset.artist || '',
    show: item.dataset.show || '',
  });

  // The picture on screen and when it appeared, for artwork_time
  let viewing = null;
  let since = 0;
  const endView = (beacon) => {
    if (!viewing) return;
    // Capped, so a lightbox left open in a forgotten tab doesn't skew it
    const seconds = Math.min(300, Math.round((Date.now() - since) / 1000));
    gtag('event', 'artwork_time', {
      ...artworkParams(viewing),
      seconds,
      ...(beacon && { transport_type: 'beacon' }),
    });
    viewing = null;
  };

  document.addEventListener('lightbox:show', ({ detail: item }) => {
    endView();
    const name = item.closest('.artwork') ? 'view_artwork' : 'view_installation';
    gtag('event', name, artworkParams(item));
    viewing = item;
    since = Date.now();
  });
  document.addEventListener('lightbox:zoom', ({ detail: item }) => gtag('event', 'zoom_artwork', artworkParams(item)));
  document.addEventListener('lightbox:close', () => endView());
  window.addEventListener('pagehide', () => endView(true));

  // Counted on arrival at the Inquire form (whose link carries the work), as
  // an event sent from the click itself is lost when the page changes
  const params = new URLSearchParams(window.location.search);
  const inquiring = document.querySelector('[data-inquire-form]') && params.get('artwork');

  const sent = () => {
    if (inquiring && !document.querySelector('[data-analytics-event]')) {
      gtag('event', 'inquire_click', {
        artwork: params.get('artwork'),
        artist: params.get('artist') || '',
        show: params.get('show') || '',
      });
    }
    document.querySelectorAll('[data-analytics-event]').forEach((el) => gtag('event', el.dataset.analyticsEvent));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sent, { once: true });
  else sent();
})();
